import type { Msg } from '@/store/slices/room';

const ALT_IMAGE_REQUEST_PATTERN = /(再来一张|另一张|换一张|再找一张|再发一张|再给我(?:找)?一张)/;
const IMAGE_SEARCH_FOLLOW_UP_PATTERN =
  /(找到了吗|找到了没|找到没|搜到了吗|搜到了没|搜到没|有了吗|有图吗|好了吗|好了没)/;
const IMAGE_REQUEST_PATTERN =
  /(图片|照片|发图|配图|来一张|给我一张|找一张|搜图|壁纸|海报|截图|猫猫|狗狗|猫咪|狗子)/;
const WEB_SEARCH_REQUEST_PATTERN =
  /(联网|搜索|检索|查一下|查一查|查查|搜一下|搜一搜|最新信息|最新消息)/;

export const SEARCH_PENDING_POST_SPEECH_DELAY_MS = 650;

type ResolvePendingSearchVisibilityStateParams = {
  messages: Msg[];
  localUserIds: string[];
  botUserIds: string[];
};

export type PendingSearchVisibilityState = {
  latestUserMessage: Msg | null;
  isPendingSearchTask: boolean;
  hasAiResponseAfterLatestUserMessage: boolean;
  latestLocalActivityAt: number | null;
  readyAt: number | null;
};

const toTimestamp = (value = '') => {
  const timestamp = new Date(value || '').getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const isImageSearchFollowUpText = (text = '') =>
  IMAGE_SEARCH_FOLLOW_UP_PATTERN.test(String(text || '').trim());

export const isWebSearchPendingText = (text = '') => {
  const normalized = String(text || '').trim();
  if (!normalized) return false;
  return (
    ALT_IMAGE_REQUEST_PATTERN.test(normalized) ||
    isImageSearchFollowUpText(normalized) ||
    IMAGE_REQUEST_PATTERN.test(normalized) ||
    WEB_SEARCH_REQUEST_PATTERN.test(normalized)
  );
};

export const resolvePendingSearchVisibilityState = ({
  messages,
  localUserIds,
  botUserIds,
}: ResolvePendingSearchVisibilityStateParams): PendingSearchVisibilityState => {
  const localUserIdSet = new Set(localUserIds.filter(Boolean));
  const botUserIdSet = new Set(botUserIds.filter(Boolean));
  const latestUserMessage =
    [...messages].reverse().find((msg) => {
      if (!localUserIdSet.has(msg.user)) return false;
      return isWebSearchPendingText(String(msg.value || ''));
    }) || null;

  if (!latestUserMessage) {
    return {
      latestUserMessage: null,
      isPendingSearchTask: false,
      hasAiResponseAfterLatestUserMessage: false,
      latestLocalActivityAt: null,
      readyAt: null,
    };
  }

  const requestAt = toTimestamp(latestUserMessage.time) ?? Date.now();
  const hasAiResponseAfterLatestUserMessage = messages.some((msg) => {
    if (!botUserIdSet.has(msg.user) || !String(msg.value || '').trim()) return false;
    const msgAt = toTimestamp(msg.time);
    return msgAt !== null && msgAt >= requestAt;
  });

  if (hasAiResponseAfterLatestUserMessage) {
    return {
      latestUserMessage,
      isPendingSearchTask: true,
      hasAiResponseAfterLatestUserMessage: true,
      latestLocalActivityAt: requestAt,
      readyAt: requestAt,
    };
  }

  const latestLocalActivity =
    [...messages].reverse().find((msg) => {
      if (!localUserIdSet.has(msg.user)) return false;
      if (!String(msg.value || '').trim() && !msg.images?.length) return false;
      const msgAt = toTimestamp(msg.time);
      return msgAt !== null && msgAt >= requestAt;
    }) || latestUserMessage;
  const latestLocalActivityAt = toTimestamp(latestLocalActivity.time) ?? requestAt;
  const delayMs = latestLocalActivity.source === 'manual' ? 0 : SEARCH_PENDING_POST_SPEECH_DELAY_MS;

  return {
    latestUserMessage,
    isPendingSearchTask: true,
    hasAiResponseAfterLatestUserMessage: false,
    latestLocalActivityAt,
    readyAt: latestLocalActivityAt + delayMs,
  };
};
